/**
 * useStreakQueries — TanStack Query hooks over the Plan 02-06 streaks API.
 *
 * Endpoint:
 *   GET /api/streaks/:memberId →
 *     { memberId, current_streak, longest_streak,
 *       streak_freezes_balance, last_activity_date }
 *
 * Cadence: staleTime 60s — streaks change at most once per Oslo day for
 * any given member; the wallet rarely needs sub-minute freshness.
 *
 * A future Plan 15 celebrations hook can call
 * `qc.invalidateQueries({queryKey: ['streaks', memberId]})` after a
 * `chore.approved` WS event arrives, so the FlameChip ticks +1 in lockstep
 * with the count-up animation on PointBalance.
 *
 * Following the Phase 2 convention (Plan 02-09 / 02-11): direct
 * `globalThis.fetch` calls — no shared lib/api wrapper.
 */
import { useQuery } from '@tanstack/react-query';

export interface StreakRow {
  memberId: string;
  current_streak: number;
  longest_streak: number;
  streak_freezes_balance: number;
  last_activity_date: string | null;
}

async function fetchJson<T>(url: string): Promise<T> {
  const r = await globalThis.fetch(url, { credentials: 'same-origin' });
  if (!r.ok) {
    const e = new Error(`fetch_failed:${r.status}`) as Error & {
      status?: number;
    };
    e.status = r.status;
    throw e;
  }
  return (await r.json()) as T;
}

export function useStreak(memberId: string, enabled = true) {
  return useQuery({
    queryKey: ['streaks', memberId],
    queryFn: async () => {
      return await fetchJson<StreakRow>(`/api/streaks/${memberId}`);
    },
    enabled: enabled && memberId.length > 0,
    staleTime: 60_000,
  });
}
